import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@shared/i18n';
import type { StoreOrder } from '@tailorcast/shared-types';
import { OrderStatusBadge } from './OrderStatusBadge';

interface OrderTableProps {
  orders: StoreOrder[];
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency }).format(value);
}

export function OrderTable({ orders }: OrderTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (orders.length === 0) {
    return <div className="text-center py-12 text-gray-500">{t('orders.empty')}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-gray-500">
            <th className="pb-3 font-medium">{t('orders.number')}</th>
            <th className="pb-3 font-medium">{t('orders.date')}</th>
            <th className="pb-3 font-medium">{t('orders.customer')}</th>
            <th className="pb-3 font-medium text-center">{t('orders.status')}</th>
            <th className="pb-3 font-medium text-right">{t('orders.total')}</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr
              key={order.id}
              onClick={() => navigate(`/orders/${order.id}`)}
              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
            >
              <td className="py-3 font-medium text-gray-900">#{order.number}</td>
              <td className="py-3 text-gray-600">
                {new Date(order.createdAt).toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
              <td className="py-3 text-gray-600">{order.customerName ?? '—'}</td>
              <td className="py-3 text-center">
                <OrderStatusBadge status={order.status} statusName={order.statusName} />
              </td>
              <td className="py-3 text-right font-medium text-gray-900">
                {formatCurrency(order.total, order.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
